import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { login as apiLogin, getNegocio, getMyRole } from '../services';
import { supabase, setBusinessId, USE_MOCK } from '../services/supabaseClient';

const AuthContext = createContext(null);

// Una hora sin interacción cierra sesión. Para un POS que maneja dinero,
// 8 horas era demasiado: si la terminal queda desatendida entre turnos,
// cualquiera con acceso físico podía operar como el usuario logueado.
const INACTIVIDAD_MS = 60 * 60 * 1000; // 1 hora
const EVENTOS        = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];

// IS_MOCK = true solo si NO hay Supabase configurado (ver supabaseClient.js).
const IS_MOCK    = USE_MOCK;
const MOCK_TOKEN = 'mezo_token';

function userFromSupabase(u) {
  if (!u) return null;
  return {
    id:            u.id,
    email:         u.email,
    role:          u.user_metadata?.role ?? null,
    planType:      u.user_metadata?.plan ?? 'Pro',
    planExpiresAt: null,
  };
}

function userFromMockJwt() {
  try {
    const token = localStorage.getItem(MOCK_TOKEN);
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      id:            payload.sub ?? payload.uid,
      email:         payload.email,
      role:          payload.role ?? null,
      planType:      payload.planType ?? 'Pro',
      planExpiresAt: payload.planExpiresAt ?? null,
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user,        setUser]        = useState(null);
  const [negocio,     setNegocio]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  // Versión de datos: al incrementar, todos los hooks de datos refetchean
  const [dataVersion, setDataVersion] = useState(0);
  const timeoutRef = useRef(null);

  const logout = useCallback(async () => {
    // Silencia cualquier toast pendiente — los hooks (useMesas/useProductos/etc.)
    // pueden tener fetches in-flight que fallarán con 401 cuando se vaya el token,
    // y mostrarían errores falsos al usuario que solo quiso cerrar sesión.
    toast.dismiss();
    setBusinessId(null);
    if (IS_MOCK) {
      localStorage.removeItem(MOCK_TOKEN);
      localStorage.removeItem('mezo_refresh_token');
      // Demo pública: al cerrar sesión también se apaga el flag — el reload
      // a '/' de abajo re-evalúa los módulos y la app vuelve al backend real.
      localStorage.removeItem('mezo_demo');
    } else {
      try { await supabase.auth.signOut(); } catch { /* noop */ }
    }
    setUser(null);
    setNegocio(null);
    setTimeout(() => { window.location.href = '/'; }, 0);
  }, []);

  const bumpVersion = useCallback(() => setDataVersion((v) => v + 1), []);

  // ── Inactividad: cierra sesión tras N horas sin interacción ───────────────

  const resetInactividad = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (user) timeoutRef.current = setTimeout(logout, INACTIVIDAD_MS);
  }, [logout, user]);

  useEffect(() => {
    EVENTOS.forEach((e) => window.addEventListener(e, resetInactividad, { passive: true }));
    resetInactividad();
    return () => {
      EVENTOS.forEach((e) => window.removeEventListener(e, resetInactividad));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [resetInactividad]);

  // ── Bootstrap de sesión ───────────────────────────────────────────────────

  useEffect(() => {
    let mounted = true;

    async function hydrate(baseUser) {
      const [n, role] = await Promise.all([
        getNegocio().catch(() => null),
        getMyRole().catch(() => null),
      ]);
      if (!mounted) return;
      setNegocio(n);
      if (role && baseUser) setUser({ ...baseUser, role });
    }

    if (IS_MOCK) {
      // Mock: leer JWT de localStorage si existe
      const u = userFromMockJwt();
      setUser(u);
      if (u) {
        hydrate(u).finally(() => mounted && setLoading(false));
      } else {
        setLoading(false);
      }
      return () => { mounted = false; };
    }

    // Real: suscribirse a auth state de Supabase (emite INITIAL_SESSION al suscribirse).
    // IMPORTANTE: el callback es SÍNCRONO. Hacer llamadas async a Supabase aquí
    // adentro causa un deadlock del lock de auth (cuelga getSession/insert).
    // Por eso diferimos hydrate() con setTimeout — corre fuera del lock.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === 'TOKEN_REFRESHED') return;

      if (!session?.user) {
        setUser(null);
        setNegocio(null);
        setBusinessId(null);
        setLoading(false);
        return;
      }

      const baseUser = userFromSupabase(session.user);
      setUser(baseUser);
      setTimeout(() => {
        if (!mounted) return;
        hydrate(baseUser).finally(() => { if (mounted) setLoading(false); });
      }, 0);
    });

    return () => { mounted = false; sub?.subscription?.unsubscribe(); };
  }, []);

  // ── Funciones públicas ────────────────────────────────────────────────────

  async function login(email, password) {
    const result = await apiLogin(email, password);
    const u      = result.user;

    if (IS_MOCK) {
      localStorage.setItem(MOCK_TOKEN, result.accessToken);
      if (result.refreshToken) localStorage.setItem('mezo_refresh_token', result.refreshToken);
    }

    const [neg, role] = await Promise.all([
      getNegocio().catch(() => null),
      getMyRole().catch(() => null),
    ]);
    const userConRol = role ? { ...u, role } : u;
    setUser(userConRol);
    setNegocio(neg);
    return { user: userConRol, negocio: neg };
  }

  async function setSession({ accessToken, refreshToken, user: u }) {
    if (IS_MOCK && accessToken) {
      localStorage.setItem(MOCK_TOKEN, accessToken);
      if (refreshToken) localStorage.setItem('mezo_refresh_token', refreshToken);
    }
    const [neg, role] = await Promise.all([
      getNegocio().catch(() => null),
      getMyRole().catch(() => null),
    ]);
    setUser(role ? { ...u, role } : u);
    setNegocio(neg);
  }

  async function refreshNegocio() {
    try {
      const [neg, role] = await Promise.all([
        getNegocio(),
        getMyRole().catch(() => null),
      ]);
      setNegocio(neg);
      if (role) setUser((u) => (u ? { ...u, role } : u));
    } catch {
      setNegocio(null);
    }
  }

  // Refresco "silencioso" del negocio — lo usa DashboardLayout al montar y al
  // volver a la pestaña, para que el plan (sidebar + usePlan) no sea la foto
  // del login si TemeT cambió el plan, venció la demo o se sirvió caché
  // offline. A diferencia de refreshNegocio, si la lectura falla o viene
  // vacía CONSERVA el negocio actual: null manda a /onboarding vía
  // ProtectedRoute y un 401 transitorio no puede sacar al dueño del POS.
  const refrescarNegocioSilencioso = useCallback(async () => {
    try {
      const neg = await getNegocio();
      if (neg) setNegocio(neg);
    } catch { /* sin red o sin sesión: nos quedamos con lo que hay */ }
  }, []);

  return (
    <AuthContext.Provider value={{
      user, negocio, loading,
      login, logout, setSession, refreshNegocio, refrescarNegocioSilencioso,
      dataVersion, bumpVersion,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
